import { useState } from 'react'
import { isAxiosError } from 'axios'
import { LoaderCircle, Play } from 'lucide-react'
import { getApiErrorMessage } from '@/lib/apiError'
import { formatSwedishDateTime } from '@/lib/format'
import { formatJobRunSource, getJobRunState } from '@/lib/jobRuns'
import { Button } from '@/components/ui/button'
import { JobRunResult } from './JobRunResult'
import { JobScheduleCell } from './JobScheduleCell'
import { JobScheduleEditor } from './JobScheduleEditor'
import { JobStatusBadge } from './JobStatusBadge'
import type { JobStatus } from '@/api/types'

interface JobRowProps {
  label: string
  description: string
  status: JobStatus
  onRun: () => Promise<void>
  onStarted: () => Promise<void>
  onSaveSchedule: (cron: string) => Promise<void>
}

const CONFLICT_STATUS = 409
const COLUMN_COUNT = 6
const RUN_LABEL = 'Kör nu'
const EMPTY_VALUE = '–'
const ALREADY_RUNNING_MESSAGE = 'Jobbet körs redan.'
const RUN_ERROR_MESSAGE = 'Kunde inte starta jobbet just nu.'

export function JobRow({ label, description, status, onRun, onStarted, onSaveSchedule }: JobRowProps): React.ReactElement {
  const [isStarting, setIsStarting] = useState(false)
  const [isEditingSchedule, setIsEditingSchedule] = useState(false)
  const [hasConflict, setHasConflict] = useState(false)
  const [runErrorMessage, setRunErrorMessage] = useState<string | null>(null)

  const run = status.last_run
  const state = getJobRunState(status.running, run)
  const isBusy = isStarting || status.running
  const isAlreadyRunningVisible = hasConflict && status.running
  const scheduleBeingEdited = isEditingSchedule ? status.schedule : null

  const handleRun = async (): Promise<void> => {
    setIsStarting(true)
    setHasConflict(false)
    setRunErrorMessage(null)

    try {
      await onRun()
      await onStarted()
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === CONFLICT_STATUS) {
        setHasConflict(true)
        await onStarted()
        return
      }

      setRunErrorMessage(getApiErrorMessage(error, RUN_ERROR_MESSAGE))
    } finally {
      setIsStarting(false)
    }
  }

  const handleSaveSchedule = async (cron: string): Promise<void> => {
    await onSaveSchedule(cron)
    setIsEditingSchedule(false)
  }

  return (
    <>
      <tr className="border-t border-border/70">
        <td className="py-4 pl-6 pr-3">
          <p className="whitespace-nowrap font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </td>
        <td className="px-3 py-4">
          <JobStatusBadge state={state} />
        </td>
        <td className="px-3 py-4">
          <JobScheduleCell
            schedule={status.schedule}
            isEditing={isEditingSchedule}
            onEdit={() => setIsEditingSchedule(true)}
          />
        </td>
        <td className="whitespace-nowrap px-3 py-4">
          {run && <p className="tabular-nums">{formatSwedishDateTime(run.finished_at)}</p>}
          {run && <p className="text-xs text-muted-foreground">{formatJobRunSource(run)}</p>}
          {!run && <span className="text-muted-foreground">{EMPTY_VALUE}</span>}
        </td>
        <td className="px-3 py-4">
          <JobRunResult run={run} />
        </td>
        <td className="py-4 pl-3 pr-6">
          <div className="flex flex-col items-end gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-none"
              disabled={isBusy}
              onClick={() => void handleRun()}
            >
              {isBusy && <LoaderCircle className="animate-spin" />}
              {!isBusy && <Play />}
              {RUN_LABEL}
            </Button>
            {isAlreadyRunningVisible && <p className="text-xs text-muted-foreground">{ALREADY_RUNNING_MESSAGE}</p>}
            {runErrorMessage && <p className="max-w-48 text-right text-xs text-destructive">{runErrorMessage}</p>}
          </div>
        </td>
      </tr>
      {scheduleBeingEdited && (
        <tr className="bg-muted/30">
          <td
            colSpan={COLUMN_COUNT}
            className="px-6 py-4"
          >
            <JobScheduleEditor
              initialCron={scheduleBeingEdited.cron}
              onSave={handleSaveSchedule}
              onCancel={() => setIsEditingSchedule(false)}
            />
          </td>
        </tr>
      )}
    </>
  )
}
